import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete, apiPut } from './client';
import type { Performance, PerformanceService, PriceOverrideEntry, PaginatedResponse, PerformancePayment, PaymentMethodValue } from './types';

// ─── PerformanceService (listino) ──────────────────────────────────────────

export function usePerformanceServices(scope: 'all' | 'global' | 'personal' = 'all') {
  return useQuery<PerformanceService[]>({
    queryKey: ['performance-services', scope],
    queryFn: () => apiGet<PerformanceService[]>(`/performances/services?scope=${scope}`),
  });
}

export function useCreatePerformanceService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; basePrice: number; cumulable?: boolean; conflictGroup?: string; scope?: 'global' | 'personal' }) => {
      const { scope, ...data } = body;
      return apiPost<PerformanceService>(`/performances/services?scope=${scope ?? 'personal'}`, data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['performance-services'] }),
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdatePerformanceService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; description?: string; basePrice?: number; cumulable?: boolean; conflictGroup?: string | null }) =>
      apiPatch<PerformanceService>(`/performances/services/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['performance-services'] }),
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeletePerformanceService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/performances/services/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['performance-services'] }),
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── Override prezzi ────────────────────────────────────────────────────────

export function useExternalOverrides(extId: string | null) {
  return useQuery<PriceOverrideEntry[]>({
    queryKey: ['external-overrides', extId],
    queryFn: () => apiGet<PriceOverrideEntry[]>(`/performances/external/${extId}/overrides`),
    enabled: !!extId,
  });
}

export function useUpsertOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ extId, serviceId, ...body }: { extId: string; serviceId: string; customPrice: number; notes?: string }) =>
      apiPut<unknown>(`/performances/external/${extId}/overrides/${serviceId}`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['external-overrides', vars.extId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeleteOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ extId, serviceId }: { extId: string; serviceId: string }) =>
      apiDelete(`/performances/external/${extId}/overrides/${serviceId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['external-overrides', vars.extId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── Performance CRUD ───────────────────────────────────────────────────────

export function usePerformances(params: { status?: string; externalId?: string; from?: string; to?: string; page?: number; limit?: number; scope?: 'my' | 'all' }) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.externalId) qs.set('externalId', params.externalId);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.scope) qs.set('scope', params.scope);
  const query = qs.toString();

  return useQuery<PaginatedResponse<Performance>>({
    queryKey: ['performances', params],
    queryFn: () => apiGet<PaginatedResponse<Performance>>(`/performances${query ? '?' + query : ''}`),
  });
}

export function usePerformance(id: string | null) {
  return useQuery<Performance>({
    queryKey: ['performance', id],
    queryFn: () => apiGet<Performance>(`/performances/${id}`),
    enabled: !!id,
  });
}

export function useCreatePerformance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title?: string; eventDate: string; location?: string;
      externalPhotographerId: string; serviceIds: string[];
      notes?: string; assignments?: { collaboratorId?: string; userId?: string; role: string; fee?: number }[];
    }) => apiPost<Performance>('/performances', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['performances'] }),
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdatePerformance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; title?: string; eventDate?: string; location?: string; serviceItems?: { performanceServiceId: string; agreedPrice?: number }[]; notes?: string; status?: string }) =>
      apiPatch<Performance>(`/performances/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['performances'] }),
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeletePerformance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/performances/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['performances'] }),
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── PerformanceAssignment ─────────────────────────────────────────────────

export function useAddAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ perfId, ...body }: { perfId: string; collaboratorId?: string; userId?: string; role: string; fee?: number }) =>
      apiPost(`/performances/${perfId}/assignments`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['performance', vars.perfId] });
      qc.invalidateQueries({ queryKey: ['performances'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useRemoveAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ perfId, assignmentId }: { perfId: string; assignmentId: string }) =>
      apiDelete(`/performances/${perfId}/assignments/${assignmentId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['performance', vars.perfId] });
      qc.invalidateQueries({ queryKey: ['performances'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── PerformancePayment (acconti) ──────────────────────────────────────────

export function usePerformancePayments(perfId: string | null) {
  return useQuery<PerformancePayment[]>({
    queryKey: ['performance-payments', perfId],
    queryFn: () => apiGet<PerformancePayment[]>(`/performances/${perfId}/payments`),
    enabled: !!perfId,
  });
}

export function useAddPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ perfId, ...body }: { perfId: string; amount: number; paidAt: string; method: PaymentMethodValue; notes?: string }) =>
      apiPost<PerformancePayment>(`/performances/${perfId}/payments`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['performance', vars.perfId] });
      qc.invalidateQueries({ queryKey: ['performance-payments', vars.perfId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeletePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ perfId, paymentId }: { perfId: string; paymentId: string }) =>
      apiDelete(`/performances/${perfId}/payments/${paymentId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['performance', vars.perfId] });
      qc.invalidateQueries({ queryKey: ['performance-payments', vars.perfId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── Self-assignment ──────────────────────────────────────────────────────────

export function useAssignMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ perfId, role, fee }: { perfId: string; role: string; fee?: number }) =>
      apiPost<unknown>(`/performances/${perfId}/assignments/me`, { role, fee }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['performance', vars.perfId] });
      qc.invalidateQueries({ queryKey: ['performances'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
